import { useCallback, useState } from 'react';

import { useToggleBrandPromotionalBannerMutation } from '@/services/innovorder/promotionalBanner/promotionalBanner.endpoints';
import type { BrandPromotionalBanner } from '@/services/innovorder/promotionalBanner/promotionalBanner.type';
import type { PickupScheduleSlot } from '@/services/innovorder/restaurant/restaurant.type';

import { isInServiceHours } from '@/pages/PromotionalBanners/utils/isInServiceHours';

type UseBrandBannerToggleParams = {
    token: string;
    brandId: number;
    schedulesMap: Record<number, PickupScheduleSlot[]>;
};

export const useBrandBannerToggle = ({ token, brandId, schedulesMap }: UseBrandBannerToggleParams) => {
    const [toggleBanner] = useToggleBrandPromotionalBannerMutation();
    const [bulkReloadState, setBulkReloadState] = useState<{
        isOpen: boolean;
        restaurantCount: number;
        onConfirm: () => void;
        onCancel: () => void;
    }>({
        isOpen: false,
        restaurantCount: 0,
        onConfirm: () => undefined,
        onCancel: () => undefined,
    });

    const executeToggle = useCallback(
        async (banner: BrandPromotionalBanner) => {
            await toggleBanner({
                token,
                brandId,
                promotionalBannerId: banner.promotionalBannerId,
                isActive: !banner.isActive,
            });
        },
        [token, brandId, toggleBanner],
    );

    const handleToggle = useCallback(
        (banner: BrandPromotionalBanner) => {
            const targetRestaurantIds = (banner.promotional_banner_brands ?? []).map((pbr) => pbr.restaurantId);
            const restaurantsInService = targetRestaurantIds.filter((rid) => {
                const schedules = schedulesMap[rid] ?? [];
                return isInServiceHours(schedules);
            });

            if (restaurantsInService.length > 0) {
                setBulkReloadState({
                    isOpen: true,
                    restaurantCount: restaurantsInService.length,
                    onConfirm: () => {
                        executeToggle(banner);
                        setBulkReloadState((prev) => ({ ...prev, isOpen: false }));
                    },
                    onCancel: () => {
                        setBulkReloadState((prev) => ({ ...prev, isOpen: false }));
                    },
                });
            } else {
                executeToggle(banner);
            }
        },
        [schedulesMap, executeToggle],
    );

    return {
        handleToggle,
        bulkReloadState,
        setBulkReloadState,
    };
};
